import { useQuery } from '@tanstack/react-query';
import { CatalogApi } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { UseQueryCustomResult } from '../types';

const useFetchEntities = (
  kind: string,
  catalogApi: CatalogApi,
): UseQueryCustomResult<Entity[]> => {
  return useQuery({
    queryKey: ['useCatalog', 'useFetchEntities', kind],
    queryFn: async () => {
      const response = await catalogApi.getEntities({
        filter: [
          {
            kind,
          },
        ],
      });
      return response.items;
    },
  });
};

// Tokenise an arbitrary string (often raw step HTML) for fuzzy-name matching.
// Strips HTML, splits on:
//   - acronym→Word boundary (URLParser → URL Parser, AXLoader → AX Loader)
//   - camelCase boundary (DataLoader → Data Loader)
//   - any non-alphanumeric character
// Lowercases, drops < 2-char tokens, dedupes.
const tokeniseRaw = (raw: string): string[] => {
  const plain = raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return Array.from(
    new Set(plain.split(/[^a-z0-9]+/).filter(t => t.length >= 2)),
  );
};

const corpusOf = (entity: Entity): string => {
  const name = entity.metadata.name ?? '';
  const title = (entity.metadata as { title?: string }).title ?? '';
  return `${name} ${title}`;
};

// Tokens shared by every candidate carry no discriminative signal — typically
// shared path components in metadata.name or common title prefixes (e.g. all
// `bdirect-datafactory-*-pipeline` entries share `bdirect`/`datafactory`/
// `pipeline` and the camelCase-split `data`/`factory`). Filtering them out
// lets the hint's actually-distinguishing tokens drive the match without
// needing a hand-curated stopword list.
const computeCommonTokens = (candidates: Entity[]): Set<string> => {
  if (candidates.length < 2) return new Set();
  const sets = candidates.map(c => new Set(tokeniseRaw(corpusOf(c))));
  const common = new Set<string>();
  for (const t of sets[0]) {
    if (sets.every(s => s.has(t))) common.add(t);
  }
  return common;
};

interface MatchScore {
  // Number of hint tokens that appear as exact tokens in the candidate.
  exact: number;
  // Sum of matched-token lengths — longer matches (e.g. "attributes") count
  // more than short ones ("ax") so substantive hits win ties on `exact`.
  exactLen: number;
  // Loose substring matches: candidate tokens that don't equal a hint token
  // but are contained inside one (e.g. `bd` ⊂ `bdirect`, `loader` ⊂
  // `dataloader`). Counted only as a tiebreaker below exact-match scoring.
  loose: number;
}

const scoreEntity = (
  entity: Entity,
  hintTokens: string[],
  commonTokens: Set<string>,
): MatchScore => {
  const candTokens = tokeniseRaw(corpusOf(entity)).filter(
    t => !commonTokens.has(t),
  );
  const candSet = new Set(candTokens);
  const hintSet = new Set(hintTokens);

  let exact = 0;
  let exactLen = 0;
  for (const h of hintTokens) {
    if (candSet.has(h)) {
      exact++;
      exactLen += h.length;
    }
  }

  let loose = 0;
  for (const c of candTokens) {
    if (hintSet.has(c)) continue; // already counted as exact
    for (const h of hintTokens) {
      if (c !== h && h.includes(c)) {
        loose++;
        break;
      }
    }
  }

  return { exact, exactLen, loose };
};

const compareScores = (a: MatchScore, b: MatchScore): number => {
  if (a.exact !== b.exact) return a.exact - b.exact;
  if (a.exactLen !== b.exactLen) return a.exactLen - b.exactLen;
  return a.loose - b.loose;
};

const findComponentByRepo = (
  components?: Entity[],
  srcRepo?: string,
  // Optional disambiguation hint — typically the BDirect step's HTML text.
  // Only consulted when more than one catalog component points at `srcRepo`;
  // matching uses dynamic-stopword filtering + token-equality scoring with
  // substring-match tiebreakers (see scoreEntity above).
  hint?: string,
): Entity | undefined => {
  if (!components || !srcRepo) {
    return undefined;
  }
  // Match `srcRepo` only when it sits between `/` boundaries inside the
  // source-location URL (or at the end). Plain `includes` collides between
  // sibling repos that share a suffix, e.g. searching for `dataloader` would
  // otherwise match `mds-dataloader`.
  const escaped = srcRepo.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`/${escaped}(?:[/?#]|$)`, 'i');
  const matches = components.filter(c => {
    const src = (
      c.metadata.annotations?.['backstage.io/source-location'] || ''
    ).toLowerCase();
    return re.test(src);
  });
  if (matches.length === 0) return undefined;
  if (matches.length === 1 || !hint) return matches[0];

  const commonTokens = computeCommonTokens(matches);
  const hintTokens = tokeniseRaw(hint).filter(t => !commonTokens.has(t));
  if (hintTokens.length === 0) return matches[0];

  let best = matches[0];
  let bestScore = scoreEntity(matches[0], hintTokens, commonTokens);
  for (let i = 1; i < matches.length; i++) {
    const s = scoreEntity(matches[i], hintTokens, commonTokens);
    // Strict > keeps the first-seen candidate on ties — stable across renders
    // since the catalog `components` list ordering is itself deterministic.
    if (compareScores(s, bestScore) > 0) {
      best = matches[i];
      bestScore = s;
    }
  }
  return best;
};

const findEntityRefByRepo = (
  components?: Entity[],
  srcRepo?: string,
): string => {
  const entityMatchBySrcLocation = findComponentByRepo(components, srcRepo);
  if (!entityMatchBySrcLocation) return '';
  const { metadata, kind } = entityMatchBySrcLocation;
  return `${metadata?.namespace || 'default'}/${kind}/${
    metadata?.name || ''
  }`.toLowerCase();
};

/**
 * Extract the repository name from a clone/web URL, e.g.
 *   https://bitbucket.org/bidone/my-app.git → "my-app"
 *   https://bitbucket.org/bidone/my-app     → "my-app"
 * Used to match Azure DevOps build repository URLs against the catalog.
 */
const repoNameFromUrl = (url?: string): string => {
  if (!url) return '';
  const cleaned = url.replace(/\.git$/i, '').replace(/\/+$/, '');
  const parts = cleaned.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1].toLowerCase() : '';
};

export {
  useFetchEntities,
  findEntityRefByRepo,
  findComponentByRepo,
  repoNameFromUrl,
};
